import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import styled from 'styled-components'

import { desktopVw } from '../../styles/utils'

import AddressModal from '../../components/Account/AddressModal'
import TextButton from '../TextButton'
import DeleteAddressModal from './DeleteAddressModal/DeleteAddressModal'
import styles from './address.module.scss'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { formatStoredPhoneNumber, getNationalPhoneForInput } from '../../utils/phone.utils'

const StyledAddress = styled.div`
  font-size: ${desktopVw(14)};
  line-height: ${desktopVw(20)};
  font-family: ${props => props.theme.fonts.grosa};
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-top: 0;
  }

  div:first-child {
    font-weight: 500;
  }
`

const AddressLine = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const Buttons = styled.div`
  width: 100%;
  display: flex;
  margin-top: ${desktopVw(20)};
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-top: auto;
  }
`

const StyledTextButton = styled(TextButton)`
  margin-right: 15px;
  font-size: ${desktopVw(14)};
  line-height: 17px;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.grosa};
  text-decoration: underline;
  text-underline-offset: 3px;
  height: min-content;
  cursor: pointer;
  &:hover {
    background-size: 0;
  }
`

const ErrorMsg = styled.p`
  font-family: ${props => props.theme.fonts.grosa};
  font-size: 14px;
  margin-bottom: 0 !important;
  color: red;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin: ${desktopVw(10)} 0 0;
    font-size: ${desktopVw(14)};
  }
`

const Address = ({
  id,
  title,
  firstName,
  lastName,
  address1,
  address2,
  city,
  provinceCode,
  province,
  phone,
  company,
  zip,
  country,
  refreshCustomerAddress,
  addressForm,
  customerId,
  stateOptions,
  isDefault,
  skipProvinceInAddress,
}) => {
  const { t } = useTranslation('accountPage')
  const [isError, setIsError] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  // `phone` here is the PhoneConfig from the locale config; renamed to avoid the
  // collision with the `phone` prop (the phone-number string) below.
  const { phone: phoneConfig } = useStatsigDynamicLocaleConfig()
  const formData = {
    firstName: firstName,
    lastName: lastName,
    address1: address1,
    address2: address2,
    province: province,
    zip: zip,
    country: country,
    company: company,
    phone: getNationalPhoneForInput(phone, phoneConfig),
    city: city,
  }

  const handleEdit = () => {
    setModalOpen(true)
  }

  const handleDelete = () => {
    setDeleteModalOpen(true)
  }

  return (
    <>
      <StyledAddress isDefault={isDefault}>
        <div className={styles.addressLineContainer}>
          <AddressLine>
            {firstName} {lastName}
          </AddressLine>
          {isDefault && <div className={styles.defaultAddressContainer}>{title}</div>}
        </div>
        <AddressLine>
          {[address1, address2, city, provinceCode, zip]
            .filter(part => part != null && typeof part === 'string' && part.trim() !== '')
            .join(' ')}
        </AddressLine>
        {phone && <AddressLine>{formatStoredPhoneNumber(phone, phoneConfig)}</AddressLine>}
        <Buttons>
          <StyledTextButton onClick={handleEdit}>{t('edit')}</StyledTextButton>
          <StyledTextButton onClick={handleDelete}>{t('delete')}</StyledTextButton>
        </Buttons>
        {isError && <ErrorMsg>{t('errInDeleting')}</ErrorMsg>}
      </StyledAddress>
      {modalOpen ? (
        <AddressModal
          open={modalOpen}
          closeModal={() => {
            setModalOpen(false)
          }}
          addressForm={addressForm}
          refreshCustomerAddress={refreshCustomerAddress}
          initialFormData={formData}
          // @i18n-ignore
          mode={'edit'}
          id={id}
          customerId={customerId}
          stateOptions={stateOptions}
          isDefault={isDefault}
          skipProvinceInAddress={skipProvinceInAddress}
        />
      ) : null}
      {deleteModalOpen ? (
        <DeleteAddressModal
          open={deleteModalOpen}
          closeModal={() => setDeleteModalOpen(false)}
          id={id}
          customerId={customerId}
          setIsError={setIsError}
          refreshCustomerAddress={refreshCustomerAddress}
        />
      ) : null}
    </>
  )
}

export default Address
